import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { cx } from './classNames';
import { EmptyState } from './EmptyState';
import { Skeleton } from './Skeleton';
import './ui-layout.css';

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  key: string;
  dir: SortDirection;
}

export type CellAlign = 'left' | 'center' | 'right';

export interface DataTableColumn<Row> {
  key: string;
  header: React.ReactNode;
  cell: (row: Row) => React.ReactNode;
  /**
   * 並び替えに使う値。渡した列だけ見出しが押せるようになる。
   * null / undefined は常に末尾へ送る。
   */
  sortValue?: (row: Row) => string | number | null | undefined;
  align?: CellAlign;
  /** 数字列。右寄せ + tabular-nums（原則 6） */
  numeric?: boolean;
  /** col の幅指定（"120px" / "20%"） */
  width?: string;
}

export interface DataTableEmpty {
  icon: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
}

export interface DataTableProps<Row> {
  columns: Array<DataTableColumn<Row>>;
  rows: Row[];
  /** 行の同一性。選択状態と React の key に使う */
  rowKey: (row: Row) => string;
  /** ツールバー左の見出し */
  title?: React.ReactNode;
  /** ツールバー右の操作（検索・追加など） */
  toolbar?: React.ReactNode;
  /** 件数ピルを出す。既定 true */
  showCount?: boolean;
  loading?: boolean;
  /** 0 件のときに出すもの。設計する対象なので必須（原則 8） */
  empty: DataTableEmpty;
  selectable?: boolean;
  onSelectionChange?: (keys: string[]) => void;
  defaultSort?: SortState;
  onRowClick?: (row: Row) => void;
  /** table の読み上げ名。省略時は title を使う */
  label?: string;
  /**
   * 640px 以下で 1 行を 1 枚のカードに畳む（原則 9: スマホでデスクトップを縮めない）。
   * 各セルの見出しは header が文字列の列だけ data-label として添える。
   */
  responsive?: boolean;
  className?: string;
}

/** セルの中のボタンや select を押したときは行クリックにしない */
const INTERACTIVE = 'button, a, input, select, textarea, label, [role="button"]';

function fromInteractive(event: React.SyntheticEvent): boolean {
  const target = event.target as HTMLElement | null;
  return Boolean(target && target !== event.currentTarget && target.closest(INTERACTIVE));
}

function compare(a: string | number | null | undefined, b: string | number | null | undefined): number {
  const aEmpty = a == null || a === '';
  const bEmpty = b == null || b === '';
  if (aEmpty && bEmpty) return 0;
  // 空は昇順・降順のどちらでも末尾（下の反転より後に効かせるため、ここでは扱わない）
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'ja');
}

const SKELETON_ROWS = 5;

/**
 * ツールバー一体型テーブル（4 パターンのひとつ）。
 * 並び替えと行選択は内蔵、ページングは持たない（取得件数はサーバ側で絞る前提）。
 */
export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  title,
  toolbar,
  showCount = true,
  loading = false,
  empty,
  selectable = false,
  onSelectionChange,
  defaultSort,
  onRowClick,
  label,
  responsive = false,
  className,
}: DataTableProps<Row>) {
  const [sort, setSort] = useState<SortState | undefined>(defaultSort);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const headerCheckbox = useRef<HTMLInputElement | null>(null);

  const keys = useMemo(() => rows.map(rowKey), [rows, rowKey]);

  // 行が入れ替わったら、もう無い行の選択を落とす
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const alive = new Set(keys);
      const next = Array.from(prev).filter((key) => alive.has(key));
      return next.length === prev.size ? prev : new Set(next);
    });
  }, [keys]);

  // 呼び出し側が毎描画で新しい関数を渡してきても効果が暴れないよう ref 経由で呼ぶ
  const notifyRef = useRef(onSelectionChange);
  notifyRef.current = onSelectionChange;
  useEffect(() => {
    notifyRef.current?.(Array.from(selected));
  }, [selected]);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const column = columns.find((c) => c.key === sort.key);
    if (!column?.sortValue) return rows;
    const getValue = column.sortValue;

    return rows.slice().sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      const aEmpty = av == null || av === '';
      const bEmpty = bv == null || bv === '';
      // 空は向きによらず末尾に固定する
      if (aEmpty || bEmpty) return compare(av, bv);
      return sort.dir === 'asc' ? compare(av, bv) : -compare(av, bv);
    });
  }, [rows, columns, sort]);

  const toggleSort = useCallback((key: string) => {
    setSort((prev) => {
      if (prev?.key !== key) return { key, dir: 'asc' };
      return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  }, []);

  const allSelected = keys.length > 0 && selected.size === keys.length;
  const someSelected = selected.size > 0 && !allSelected;

  useEffect(() => {
    if (headerCheckbox.current) headerCheckbox.current.indeterminate = someSelected;
  }, [someSelected]);

  const toggleAll = () => {
    setSelected((prev) => (prev.size === keys.length ? new Set<string>() : new Set(keys)));
  };

  const toggleOne = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const columnCount = columns.length + (selectable ? 1 : 0);
  const hasToolbar = title != null || toolbar != null || showCount;

  return (
    <div className={cx('ui-table-wrap', responsive && 'ui-table-wrap--cards', className)}>
      {hasToolbar && (
        <div className="ui-table__toolbar">
          <div className="ui-table__toolbar-start">
            {title != null && <h2 className="ui-table__title">{title}</h2>}
            {showCount && (
              <span className="ui-table__count" data-testid="ui-table-count">
                {loading ? '—' : `${rows.length} 件`}
              </span>
            )}
            {selected.size > 0 && (
              <span className="ui-table__selected" data-testid="ui-table-selected">
                {selected.size} 件選択中
              </span>
            )}
          </div>
          {toolbar != null && <div className="ui-table__toolbar-end">{toolbar}</div>}
        </div>
      )}

      <div className="ui-table__scroll">
        <table className="ui-table" aria-label={label ?? (typeof title === 'string' ? title : undefined)}>
          <colgroup>
            {selectable && <col style={{ width: '44px' }} />}
            {columns.map((column) => (
              <col key={column.key} style={column.width ? { width: column.width } : undefined} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {selectable && (
                <th scope="col" className="ui-table__cell--check">
                  <input
                    ref={headerCheckbox}
                    type="checkbox"
                    className="ui-checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={keys.length === 0}
                    aria-label="すべて選択"
                  />
                </th>
              )}
              {columns.map((column) => {
                const sortable = Boolean(column.sortValue);
                const active = sort?.key === column.key;
                const align = column.align ?? (column.numeric ? 'right' : 'left');

                return (
                  <th
                    key={column.key}
                    scope="col"
                    className={cx(`ui-table__cell--${align}`, column.numeric && 'ui-table__cell--num')}
                    aria-sort={active ? (sort?.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        className={cx('ui-table__sort', active && 'ui-table__sort--active')}
                        onClick={() => toggleSort(column.key)}
                      >
                        <span>{column.header}</span>
                        {active ? (
                          sort?.dir === 'asc' ? (
                            <ChevronUp aria-hidden="true" />
                          ) : (
                            <ChevronDown aria-hidden="true" />
                          )
                        ) : (
                          <ChevronsUpDown aria-hidden="true" />
                        )}
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {loading &&
              Array.from({ length: SKELETON_ROWS }, (_, i) => (
                <tr key={`skeleton-${i}`} data-testid="ui-table-skeleton-row">
                  {selectable && <td className="ui-table__cell--check" />}
                  {columns.map((column) => (
                    <td key={column.key}>
                      <Skeleton />
                    </td>
                  ))}
                </tr>
              ))}

            {!loading && sortedRows.length === 0 && (
              <tr>
                <td colSpan={columnCount} className="ui-table__empty">
                  <EmptyState
                    icon={empty.icon}
                    title={empty.title}
                    description={empty.description}
                    action={empty.action}
                  />
                </td>
              </tr>
            )}

            {!loading &&
              sortedRows.map((row) => {
                const key = rowKey(row);
                const isSelected = selected.has(key);

                return (
                  <tr
                    key={key}
                    className={cx(
                      isSelected && 'ui-table__row--selected',
                      onRowClick && 'ui-table__row--clickable',
                    )}
                    data-testid="ui-table-row"
                    // 行クリックは補助導線。キーボードでも同じことができるようにするが、
                    // 行の意味を壊さないよう role は table のままにしておく（Phase 7 で再検討）
                    tabIndex={onRowClick ? 0 : undefined}
                    onClick={
                      onRowClick
                        ? (event) => {
                            if (fromInteractive(event)) return;
                            onRowClick(row);
                          }
                        : undefined
                    }
                    onKeyDown={
                      onRowClick
                        ? (event) => {
                            if (fromInteractive(event)) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onRowClick(row);
                            }
                          }
                        : undefined
                    }
                  >
                    {selectable && (
                      <td className="ui-table__cell--check">
                        <input
                          type="checkbox"
                          className="ui-checkbox"
                          checked={isSelected}
                          onChange={() => toggleOne(key)}
                          onClick={(event) => event.stopPropagation()}
                          aria-label={`${key} を選択`}
                        />
                      </td>
                    )}
                    {columns.map((column) => {
                      const align = column.align ?? (column.numeric ? 'right' : 'left');
                      return (
                        <td
                          key={column.key}
                          className={cx(`ui-table__cell--${align}`, column.numeric && 'ui-table__cell--num')}
                          data-label={typeof column.header === 'string' ? column.header : undefined}
                        >
                          {column.cell(row)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
